import { useState } from "react";
import {
    Alert, Box, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT, useTRich } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import AmberLink from "src/components/AmberLink";
import BusyButton from "src/components/BusyButton";
import { reaffirmAcceptances } from "src/data/legalDocument";

/**
 * Login flow for an email that already exists in the system. Entered after
 * EmailStep's checkEmail probe returns isAvailable=false. The caller passes:
 *   - email          confirmed-existing address (read-only here)
 *   - hasPassword    whether the account already has a password. true → offer the
 *                    "Salasana" + "Unohtuiko salasana?" branches. false (OAuth-only,
 *                    e.g. a Microsoft sign-up) → offer an "Aseta salasana" branch
 *                    that sets an INITIAL password through the same reset-code flow,
 *                    giving the user a password login alongside their SSO provider.
 *   - onBack         exit back to the email step (lets the user fix the address)
 */
export default function LoginFlow({ email, hasPassword, onBack }) {
    const { login, sendLoginCode, loginWithEmailCode, requestPasswordResetCode, verifyPasswordResetCode } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const tRich = useTRich();

    // method: "password" | "code" | "reset"
    const [method, setMethod] = useState(hasPassword ? "password" : "code");
    // subStep: "request" | "verify". Used by both "code" and "reset" (they
    // share the same shape: send a code, then enter it). "password" ignores it.
    const [subStep, setSubStep] = useState("request");

    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [sessionId, setSessionId] = useState(null);

    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const resetTransient = () => {
        setError(null);
        setCode("");
        setPassword("");
        setNewPassword("");
        setSessionId(null);
    };

    const handleMethodChange = (_, next) => {
        if (!next || next === method) return;
        setMethod(next);
        setSubStep("request");
        resetTransient();
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
            const data = await login(email, password);
            reaffirmAcceptances(data);
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.loginFailed" }));
        } finally {
            setBusy(false);
        }
    };

    const submitSendCode = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
            const sid = await sendLoginCode(email);
            setSessionId(sid);
            setSubStep("verify");
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.codeSendFailed" }));
        } finally {
            setBusy(false);
        }
    };

    const submitVerifyCode = async (e) => {
        e.preventDefault();
        if (!code.trim()) { setError(t("auth.login.codeRequired")); return; }
        setBusy(true); setError(null);
        try {
            const data = await loginWithEmailCode({ email, code: code.trim(), sessionId });
            reaffirmAcceptances(data);
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.codeLoginFailed" }));
        } finally {
            setBusy(false);
        }
    };

    const submitRequestReset = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
            await requestPasswordResetCode(email);
            setSubStep("verify");
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.resetCodeSendFailed" }));
        } finally {
            setBusy(false);
        }
    };

    // Two-step submit so we can distinguish "reset failed" (user retries
    // the code) from "reset succeeded but login failed" (password IS already
    // changed — direct the user back to the Salasana tab to retry the login
    // alone). Without this split a network blip on the second call would
    // make the user believe the reset itself failed and re-submit it.
    const submitVerifyReset = async (e) => {
        e.preventDefault();
        if (!code.trim()) { setError(t("auth.login.resetCodeRequired")); return; }
        if (newPassword.length < 8) { setError(t("auth.login.passwordMinLength")); return; }
        setBusy(true); setError(null);
        try {
            await verifyPasswordResetCode({ email, code: code.trim(), newPassword });
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.passwordChangeFailed" }));
            setBusy(false);
            return;
        }
        try {
            const data = await login(email, newPassword);
            reaffirmAcceptances(data);
        } catch (err) {
            const reason = resolveErrorMessage(err, { t, lang, fallbackKey: "errors.networkError" });
            setError(t(hasPassword ? "auth.passwordChangedLoginFailed" : "auth.passwordSetLoginFailed", { reason }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 480 }}>
            <Stack spacing={3}>
                <Typography variant="h4" component="h1">{t("auth.login.title")}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("auth.login.emailPrefix")} <strong>{email}</strong>{" "}
                    <AmberLink onClick={onBack}>{t("auth.login.changeEmail")}</AmberLink>
                </Typography>

                <ToggleButtonGroup
                    value={method}
                    exclusive
                    onChange={handleMethodChange}
                    size="small"
                    color="warning"
                    fullWidth
                >
                    {hasPassword && <ToggleButton value="password">{t("auth.shared.passwordLabel")}</ToggleButton>}
                    <ToggleButton value="code">{t("auth.login.emailCodeMethod")}</ToggleButton>
                    <ToggleButton value="reset">{hasPassword ? t("auth.login.forgotPasswordMethod") : t("auth.login.setPasswordMethod")}</ToggleButton>
                </ToggleButtonGroup>

                {error && <Alert severity="error">{error}</Alert>}

                {method === "password" && (
                    <Box component="form" onSubmit={submitPassword}>
                        <Stack spacing={2}>
                            <TextField
                                label={t("auth.shared.passwordLabel")} type="password" required fullWidth autoFocus
                                value={password} onChange={(e) => setPassword(e.target.value)}
                            />
                            <BusyButton type="submit" size="large" variant="contained" color="warning" loading={busy}>
                                {busy ? t("auth.shared.loggingIn") : t("auth.shared.loginButton")}
                            </BusyButton>
                        </Stack>
                    </Box>
                )}

                {method === "code" && subStep === "request" && (
                    <Box component="form" onSubmit={submitSendCode}>
                        <Stack spacing={2}>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {tRich("auth.login.sendCodeIntro", { email: <strong>{email}</strong> })}
                            </Typography>
                            <BusyButton type="submit" size="large" variant="contained" color="warning" loading={busy}>
                                {busy ? t("auth.login.sending") : t("auth.login.sendCodeButton")}
                            </BusyButton>
                        </Stack>
                    </Box>
                )}

                {method === "code" && subStep === "verify" && (
                    <Box component="form" onSubmit={submitVerifyCode}>
                        <Stack spacing={2}>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {tRich("auth.login.codeSentTo", { email: <strong>{email}</strong> })}
                            </Typography>
                            <TextField
                                label={t("auth.login.codeFieldLabel")} required fullWidth autoFocus
                                value={code} onChange={(e) => setCode(e.target.value)}
                                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 4 } }}
                            />
                            <BusyButton type="submit" size="large" variant="contained" color="warning" loading={busy}>
                                {busy ? t("auth.shared.loggingIn") : t("auth.login.loginWithCodeButton")}
                            </BusyButton>
                            <Button size="small" onClick={() => { setSubStep("request"); resetTransient(); }}>
                                {t("auth.login.resendCode")}
                            </Button>
                        </Stack>
                    </Box>
                )}

                {method === "reset" && subStep === "request" && (
                    <Box component="form" onSubmit={submitRequestReset}>
                        <Stack spacing={2}>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {hasPassword
                                    ? tRich("auth.login.resetIntroWithPassword", { email: <strong>{email}</strong> })
                                    : tRich("auth.login.resetIntroSetPassword", { email: <strong>{email}</strong> })}
                            </Typography>
                            <BusyButton type="submit" size="large" variant="contained" color="warning" loading={busy}>
                                {busy ? t("auth.login.sending") : (hasPassword ? t("auth.login.sendResetCodeButton") : t("auth.login.sendVerifyCodeButton"))}
                            </BusyButton>
                        </Stack>
                    </Box>
                )}

                {method === "reset" && subStep === "verify" && (
                    <Box component="form" onSubmit={submitVerifyReset}>
                        <Stack spacing={2}>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {hasPassword ? t("auth.login.resetVerifyIntroWithPassword") : t("auth.login.resetVerifyIntroSetPassword")}
                            </Typography>
                            <TextField
                                label={hasPassword ? t("auth.login.resetCodeLabel") : t("auth.shared.verificationCodeLabel")} required fullWidth autoFocus
                                value={code} onChange={(e) => setCode(e.target.value)}
                                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 4 } }}
                            />
                            <TextField
                                label={hasPassword ? t("auth.login.newPasswordLabel") : t("auth.shared.passwordLabel")} type="password" required fullWidth
                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                helperText={t("auth.shared.min8Chars")}
                            />
                            <BusyButton type="submit" size="large" variant="contained" color="warning" loading={busy}>
                                {busy
                                    ? (hasPassword ? t("auth.login.changingPassword") : t("auth.login.settingPassword"))
                                    : (hasPassword ? t("auth.login.changePasswordAndLogin") : t("auth.login.setPasswordAndLogin"))}
                            </BusyButton>
                            <Button size="small" onClick={() => { setSubStep("request"); resetTransient(); }}>
                                {hasPassword ? t("auth.login.resendResetCode") : t("auth.login.resendVerifyCode")}
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
